/**
 * Web layers (posthog-js) for the platform-neutral analytics tags in
 * `./analytics-tag`.
 *
 * React components should prefer the `useFlag()` / `useAnalytics()` hooks —
 * these layers are for non-React Effect code that needs analytics on the
 * client. Add them to the client ManagedRuntime:
 * ```typescript
 * Layer.mergeAll(ApiClient.layer, AnalyticsWebLive, FeatureFlagsWebLive)
 * ```
 * and consume via the tags:
 * ```typescript
 * const analytics = yield* Analytics;
 * yield* analytics.capture('some_event', { key: 'value' });
 * ```
 */
import { Effect, Layer } from 'effect';
import { posthog } from 'posthog-js';
import { Analytics, FeatureFlags } from './analytics-tag';
import { getFlagValue, clearAllLocalFlags } from './client';

const DEFAULT_PROPERTIES = { platform: 'web' as const };

export const AnalyticsWebLive = Layer.succeed(
  Analytics,
  Analytics.of({
    identify: (userId, traits) =>
      Effect.sync(() => posthog.identify(userId, traits)),

    reset: Effect.sync(() => {
      posthog.reset();
      clearAllLocalFlags();
    }),

    capture: (event, properties) =>
      Effect.sync(() =>
        posthog.capture(event, {
          ...DEFAULT_PROPERTIES,
          ...(properties as Record<string, unknown>),
        }),
      ),
  }),
);

export const FeatureFlagsWebLive = Layer.succeed(
  FeatureFlags,
  FeatureFlags.of({
    getFlag: (flag) => Effect.sync(() => getFlagValue(flag)),

    hasLoadedFlags: Effect.sync(() => posthog.featureFlags?.hasLoadedFlags),
  }),
);
